import { useAuthStore } from '@/stores/auth';
import { LogOut } from 'lucide-react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { exitImpersonationOrSignOut } from '@/lib/exit-impersonation';
import { leaveForIdentitySwitch } from '@/lib/identity-switch';
import { toast } from 'sonner';
import { useState } from 'react';

export function ImpersonationBanner() {
  const { user, isImpersonating, exitImpersonation, logout } = useAuthStore();
  const formatName = useEmployeeNameFormatter();
  // Held from the click until the page goes away: a second click would send a
  // second exit and stack a second notice and reload. Released only when the
  // exit failed without an answer and may be tried again. [HW-942]
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating || !user) return null;

  const displayName = user.employee
    ? formatName({ firstName: user.employee.firstName, lastName: user.employee.lastName })
    : user.email;

  // A refused exit used to be swallowed, leaving a button that did nothing;
  // `exitImpersonationOrSignOut` now answers each refusal. [HW-942] A
  // successful exit reloads the page onto /people from inside the store. [HW-872]
  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    const outcome = await exitImpersonationOrSignOut({
      exit: exitImpersonation,
      logout,
      notify: (message) => toast.error(message),
      // `logout()` signs out and `AuthGuard` then moves the app to /login; this
      // full reload, after the toast has had a moment on screen, drops every
      // in-memory store the impersonated session filled. [HW-942] `replace` gives
      // the page being left no history entry of its own; an earlier one that Back
      // restores from the browser's cache reloads (`reloadPagesRestoredFromCache`). [HW-872]
      toLogin: () => {
        setTimeout(() => {
          leaveForIdentitySwitch('/login');
        }, 2500);
      },
      // The cookies already hold the admin's own session; the reload drops what
      // this tab still shows of the impersonated one.
      toHome: () => {
        setTimeout(() => {
          leaveForIdentitySwitch('/');
        }, 2500);
      },
    });
    if (outcome === 'stayed') setExiting(false);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] flex h-9 items-center justify-between bg-amber-500 px-4 text-sm font-medium text-amber-950">
      <div className="flex items-center gap-2">
        <span>
          Viewing as <strong>{displayName}</strong>
          <span className="ml-1 opacity-75">&middot; {user.role}</span>
        </span>
      </div>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="inline-flex items-center gap-1.5 rounded-md bg-amber-950/15 px-3 py-1 text-xs font-semibold transition-colors hover:bg-amber-950/25"
      >
        <LogOut className="h-3 w-3" />
        Exit
      </button>
    </div>
  );
}
